import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spin, Progress, Space, Divider, Result, Statistic, Row, Col, Modal, Typography, message } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, LoadingOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

const { Text } = Typography;
import { surveyService } from '../../services/survey.service';
import { RichTextDisplay } from '../../components/common/RichTextDisplay';
import responseService from '../../services/response.service';
import { Survey, Question, QuestionType } from '../../types/survey.types';
import { AnswerData, AnswerType } from '../../types/response.types';
import MultipleChoiceQuestion from '../../components/survey/MultipleChoiceQuestion';
import CheckboxQuestion from '../../components/survey/CheckboxQuestion';
import TextQuestion from '../../components/survey/TextQuestion';
import NumericQuestion from '../../components/survey/NumericQuestion';
import RatingQuestion from '../../components/survey/RatingQuestion';
import SubmissionConfirmation from '../../components/survey/SubmissionConfirmation';
import { parseApiError } from '../../utils/error-handler';
import { useDeviceData } from '../../hooks/useDeviceData';
import { geoConsentService } from '../../services/geoConsent.service';
import { surveyQuestionGroupService } from '../../services/survey-question-group.service';
import { marketingService } from '../../services/marketing.service';
import { useAuth } from '../../hooks/useAuth';
import { useGuestSession } from '../../services/useGuestSession';
import { PersonalizedGroupSequence } from '../../types/survey-question-group.types';
import { QuestionGroupNav, GroupProfileIncompleteMessage, GroupNoGroupsMessage } from '../../components/survey/QuestionGroupNav';
import { QuestionFilterBar } from '../../components/survey/QuestionFilterBar';
import {
  NULL_OPTION_VALUE,
  DONT_KNOW_OPTION_VALUE,
  filterChoices,
  shouldHideOptionsUntilFiltered,
  keepOnlySpecialChoices,
  countRegularChoices,
} from '../../utils/questionOptions';
import { EffectiveChoiceItem } from '../../types/survey.types';

// Phase 6 - US4: Take Survey Page (T114, T117, T118)

const TakeSurvey = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const guestSession = useGuestSession();
  const { collect, prewarm } = useDeviceData();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Map<string, AnswerData>>(new Map());
  // Spec 033: filtros de resposta (botões + texto digitado) por pergunta, independentes
  // das respostas já marcadas.
  const [questionFilters, setQuestionFilters] = useState<
    Map<string, { selectedFilters: string[]; filterText: string }>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [responseHash, setResponseHash] = useState<string | null>(null);
  const [profileStale, setProfileStale] = useState(false);
  const [geoModalVisible, setGeoModalVisible] = useState(false);
  const [geoChoice, setGeoChoice] = useState(false);

  // Group navigation state
  const [groupSequence, setGroupSequence] = useState<PersonalizedGroupSequence | null>(null);
  const [groupProfileIncomplete, setGroupProfileIncomplete] = useState(false);
  const [noGroupsAvailable, setNoGroupsAvailable] = useState(false);
  const [currentGroupIdx, setCurrentGroupIdx] = useState(0);

  useEffect(() => {
    if (surveyId) {
      loadSurveyData();
      checkEligibility();
      loadGroupSequence(surveyId);
    }
  }, [surveyId]);

  const loadGroupSequence = async (sid: string) => {
    try {
      const seq = await surveyQuestionGroupService.getPersonalizedSequence(sid);
      if (seq === null) {
        setNoGroupsAvailable(true);
      } else {
        setGroupSequence(seq);
      }
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setGroupProfileIncomplete(true);
      }
      // Any other error: fall back to flat list (groupSequence stays null)
    }
  };

  // Show geo consent modal as soon as the survey is ready so GPS collection
  // starts early — by submit time the coordinates are already cached.
  useEffect(() => {
    if (!loading && survey && !error && !profileStale) {
      const saved = geoConsentService.getPreference();
      if (saved !== null) {
        setGeoChoice(saved.allowed);
        prewarm(saved.allowed);
      } else {
        setGeoModalVisible(true);
      }
    }
  }, [loading, survey, error, profileStale]);

  const loadSurveyData = async () => {
    try {
      setLoading(true);
      const surveyData = await surveyService.getSurveyForTaking(surveyId!);

      setSurvey(surveyData as Survey);
      setQuestions(surveyData.questions || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  // T117: Check profile staleness and eligibility (T118: duplicate check)
  const checkEligibility = async () => {
    try {
      const eligibility = await responseService.checkEligibility(surveyId!);
      if (!eligibility.eligible) {
        const reason = eligibility.reason || '';
        const reasonLower = reason.toLowerCase();

        if (
          reasonLower.includes('stale') ||
          reasonLower.includes('90 days') ||
          reasonLower.includes('desatualizado') ||
          reasonLower.includes('90 dias')
        ) {
          setProfileStale(true);
        } else if (reasonLower.includes('already responded') || reasonLower.includes('já respondeu')) {
          setError('Você já respondeu a esta pesquisa.');
        } else {
          setError(eligibility.reason || 'Você não é elegível para esta pesquisa');
        }
      }
    } catch (err: any) {
      console.error('Eligibility check failed', err);
      setError('Não foi possível verificar sua elegibilidade para esta pesquisa.');
    }
  };

  const handleAnswerChange = (questionId: string, answer: AnswerData) => {
    setAnswers(new Map(answers.set(questionId, answer)));
  };

  const getQuestionFilterState = (questionId: string) =>
    questionFilters.get(questionId) || { selectedFilters: [], filterText: '' };

  const setQuestionSelectedFilters = (questionId: string, selectedFilters: string[]) => {
    const current = getQuestionFilterState(questionId);
    setQuestionFilters(new Map(questionFilters.set(questionId, { ...current, selectedFilters })));
  };

  const setQuestionFilterText = (questionId: string, filterText: string) => {
    const current = getQuestionFilterState(questionId);
    setQuestionFilters(new Map(questionFilters.set(questionId, { ...current, filterText })));
  };

  const getQuestionChoices = (question: Question): EffectiveChoiceItem[] =>
    question.effectiveChoices && question.effectiveChoices.length > 0
      ? question.effectiveChoices
      : (question.options?.choices || []).map((choice) => ({ ...choice, kind: 'base' as const }));

  // Group navigation helpers
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleGroupNext = () => {
    setCurrentGroupIdx((i) => i + 1);
    scrollToTop();
  };

  const handleGroupPrev = () => {
    setCurrentGroupIdx((i) => i - 1);
    scrollToTop();
  };

  const currentGroup = groupSequence ? groupSequence.groups[currentGroupIdx] : null;

  // Resolve which questions belong to the current group using two strategies:
  // 1. Direct FK: q.questionGroupId matches the group's DB id (questions assigned via the editor)
  // 2. Sequence list: the backend group sequence includes the question ids (set via questionGroupId)
  // Strategy 1 is preferred; strategy 2 is the fallback for backward compatibility.
  const visibleQuestions = (() => {
    if (!currentGroup) return questions;

    const byGroupId = questions.filter(
      (q) => String((q as any).questionGroupId) === String(currentGroup.groupId),
    );
    if (byGroupId.length > 0) return byGroupId;

    return questions.filter((q) =>
      currentGroup.questions.some((gq) => String(gq.questionId) === String(q.id)),
    );
  })();

  // canAdvance: all required questions in the CURRENT visible group must be answered
  const groupCanAdvance = (() => {
    if (!currentGroup) return true;
    return visibleQuestions
      .filter((q) => q.required)
      .every((q) => {
        const a = answers.get(q.id);
        if (!a) return false;
        if (a.answerType === 'CHOICES') {
          if ((a.choiceAnswers?.length ?? 0) === 0) return false;
          if (q.questionType === QuestionType.CHECKBOX) {
            const maxSel = q.options?.maxSelections ?? 0;
            if (maxSel > 0 && q.options?.enforceSelectionLimit) {
              const hasSpecial = a.choiceAnswers?.some(
                (v) => v === NULL_OPTION_VALUE || v === DONT_KNOW_OPTION_VALUE
              ) ?? false;
              if ((a.choiceAnswers?.length ?? 0) < maxSel && !hasSpecial) return false;
            }
          }
          return true;
        }
        if (a.answerType === 'TEXT') return (a.textAnswer?.trim().length ?? 0) > 0;
        if (a.answerType === 'NUMBER') return a.numericAnswer !== undefined && a.numericAnswer !== null;
        if (a.answerType === 'CHOICE') return (a.choiceAnswers?.length ?? 0) > 0;
        return true;
      });
  })();

  const validateForm = (): boolean => {
    let requiredQuestions: Question[];

    if (hasGroups && groupSequence) {
      // Only validate required questions from groups visible to this participant.
      // Uses same two-strategy resolution as visibleQuestions:
      //   1. q.questionGroupId matches one of the participant's visible group IDs
      //   2. fallback: question appears in the group sequence question list
      const visibleGroupIds = new Set(groupSequence.groups.map((g) => String(g.groupId)));
      const seqQuestionIds = new Set(
        groupSequence.groups.flatMap((g) => g.questions.map((gq) => String(gq.questionId))),
      );

      requiredQuestions = questions.filter((q) => {
        if (!q.required) return false;
        const assignedGroupId = String((q as any).questionGroupId);
        if (visibleGroupIds.has(assignedGroupId)) return true;  // strategy 1
        if (seqQuestionIds.has(String(q.id))) return true;       // strategy 2
        return false;
      });
    } else {
      requiredQuestions = questions.filter((q) => q.required);
    }

    const answeredIds = Array.from(answers.keys());
    for (const question of requiredQuestions) {
      if (!answeredIds.includes(question.id)) {
        message.warning(`Por favor, responda a pergunta obrigatória: ${question.shortDescription}`);
        return false;
      }
    }

    return true;
  };

  const handleGeoChoice = (allowGps: boolean) => {
    geoConsentService.setPreference(allowGps);
    setGeoModalVisible(false);
    setGeoChoice(allowGps);
    prewarm(allowGps);
  };

  const handleSubmit = () => {
    setError('');
    if (!validateForm()) return;
    submitWithCachedData();
  };

  const submitWithCachedData = async () => {
    try {
      setSubmitting(true);
      const deviceData = await collect(geoChoice);
      const guestToken = guestSession.isActive ? guestSession.token ?? undefined : undefined;
      const result = await responseService.submitResponse({
        surveyId: surveyId!,
        answers: Array.from(answers.values()),
        deviceFingerprint: deviceData.fingerprint,
        geoLatitude: deviceData.geoLatitude,
        geoLongitude: deviceData.geoLongitude,
        geoConsentGiven: deviceData.geoConsentGiven,
      }, guestToken);

      if (!result?.responseHash) {
        message.error('Falha: resposta não contém hash válido');
        return;
      }

      marketingService.pushEvent('pesquisa_respondida', { survey_id: surveyId!, user_id: user?.userId ?? '' });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['participant-active'] }),
        queryClient.invalidateQueries({ queryKey: ['participant-unanswered'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-tabs'] }),
        queryClient.invalidateQueries({ queryKey: ['participant-referral-link'] }),
        queryClient.invalidateQueries({ queryKey: ['engagement'] }),
      ]);

      setResponseHash(result.responseHash);
      message.success('Resposta enviada com sucesso!');
    } catch (err: any) {
      const apiError = parseApiError(err);
      message.error(apiError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question, hideRegularChoices = false) => {
    const answer = answers.get(question.id);

    switch (question.questionType) {
      case QuestionType.MULTIPLE_CHOICE: {
        const { selectedFilters, filterText } = getQuestionFilterState(question.id);
        const filtered = filterChoices(getQuestionChoices(question), selectedFilters, filterText);
        const visibleChoices = hideRegularChoices ? keepOnlySpecialChoices(filtered) : filtered;
        return (
          <MultipleChoiceQuestion
            question={question}
            value={answer?.choiceAnswers?.[0]}
            visibleChoices={visibleChoices}
            onChange={(value) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.CHOICE,
                choiceAnswers: [value],
              })
            }
          />
        );
      }

      case QuestionType.CHECKBOX: {
        const { selectedFilters, filterText } = getQuestionFilterState(question.id);
        const filtered = filterChoices(getQuestionChoices(question), selectedFilters, filterText);
        const visibleChoices = hideRegularChoices ? keepOnlySpecialChoices(filtered) : filtered;
        return (
          <CheckboxQuestion
            question={question}
            values={answer?.choiceAnswers || []}
            visibleChoices={visibleChoices}
            onChange={(values) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.CHOICES,
                choiceAnswers: values,
              })
            }
          />
        );
      }

      case QuestionType.TEXT:
        return (
          <TextQuestion
            question={question}
            value={answer?.textAnswer || ''}
            onChange={(value) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.TEXT,
                textAnswer: value,
              })
            }
          />
        );

      case QuestionType.RATING:
        return (
          <RatingQuestion
            question={question}
            value={answer?.numericAnswer}
            onChange={(value) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.NUMBER,
                numericAnswer: value,
              })
            }
          />
        );

      case QuestionType.NUMBER:
        return (
          <NumericQuestion
            question={question}
            value={answer?.numericAnswer}
            onChange={(value) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.NUMBER,
                numericAnswer: value,
              })
            }
          />
        );

      case QuestionType.DATE:
        return (
          <TextQuestion
            question={question}
            value={answer?.dateAnswer || ''}
            onChange={(value) =>
              handleAnswerChange(question.id, {
                questionId: question.id,
                answerType: AnswerType.DATE,
                dateAnswer: value,
              })
            }
            type="date"
          />
        );

      default:
        return <div>Tipo de pergunta não suportado</div>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }

  if (responseHash) {
    return <SubmissionConfirmation responseHash={responseHash} surveyName={survey?.name || ''} />;
  }

  // T117: Profile staleness warning
  if (profileStale) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Result
          status="warning"
          title="Perfil Desatualizado"
          subTitle="Seu perfil não foi atualizado nos últimos 90 dias. Por favor, atualize suas informações antes de participar da pesquisa."
          extra={
            <Button type="primary" size="large" onClick={() => navigate('/profile')}>
              Atualizar Perfil
            </Button>
          }
        />
      </div>
    );
  }

  const isProfileMissing = !!(error && (
    error.toLowerCase().includes('perfil não encontrado') ||
    error.toLowerCase().includes('profile not found')
  ));

  if (isProfileMissing) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Result
          status="info"
          title="Complete seu perfil para participar"
          subTitle="Esta pesquisa requer que seu perfil esteja preenchido. Leva poucos minutos e você será redirecionado de volta automaticamente."
          extra={
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(`/profile?next=/surveys/${surveyId}/take`)}
            >
              Completar Perfil
            </Button>
          }
        />
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Alert type="error" message="Erro" description={error || 'Pesquisa não encontrada'} />
      </div>
    );
  }

  // Profile incomplete for groups (not profile staleness)
  if (groupProfileIncomplete) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <GroupProfileIncompleteMessage />
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/profile')}>
            Atualizar Perfil
          </Button>
        </div>
      </div>
    );
  }

  const hasGroups = groupSequence !== null && groupSequence.totalGroups > 0;
  const hasConfiguredGroups = questions.some(
    (q) => (q as any).questionGroupId !== null && (q as any).questionGroupId !== undefined,
  );
  const progress = hasGroups
    ? Math.round(((currentGroupIdx + 1) / groupSequence!.totalGroups) * 100)
    : Math.round((answers.size / questions.length) * 100);
  const isLastGroup = hasGroups && currentGroupIdx === groupSequence!.groups.length - 1;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Survey Header Card */}
        <Card className="shadow-md">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{survey.name}</h1>
        {survey.description && <RichTextDisplay html={survey.description} className="text-gray-600 mb-4" />}

        <Divider />

        {hasGroups ? (
          <QuestionGroupNav
            currentGroupIndex={currentGroupIdx}
            totalGroups={groupSequence!.totalGroups}
            isFirstGroup={currentGroupIdx === 0}
            isLastGroup={isLastGroup}
            canAdvance={groupCanAdvance}
            loading={submitting}
            onNext={handleGroupNext}
            onPrev={handleGroupPrev}
            onSubmit={handleSubmit}
          />
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                <Card>
                  <Statistic title="Perguntas" value={questions.length} valueStyle={{ color: '#1890ff' }} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                <Card>
                  <Statistic title="Respondidas" value={answers.size} valueStyle={{ color: '#52c41a' }} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                <Card>
                  <Statistic title="Faltando" value={questions.length - answers.size} valueStyle={{ color: '#fa8c16' }} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                <Card>
                  <Statistic title="Progresso" value={progress} suffix="%" valueStyle={{ color: '#722ed1' }} />
                </Card>
              </Col>
            </Row>
            <Progress
              percent={progress}
              strokeColor={{ '0%': '#1890ff', '100%': '#52c41a' }}
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Card>

      {/* Group name (if configured to show) */}
      {hasGroups && currentGroup?.displayName && (
        <Card size="small" style={{ background: '#f0f4ff', borderColor: '#adc6ff' }}>
          <strong>{currentGroup.displayName}</strong>
        </Card>
      )}

      {/* Only show "no groups" when the survey actually uses grouped questions */}
      {noGroupsAvailable && hasConfiguredGroups && <GroupNoGroupsMessage />}

      {/* Questions (group-filtered or flat) */}
      {visibleQuestions.map((question, index) => {
        const isChoiceType =
          question.questionType === QuestionType.MULTIPLE_CHOICE || question.questionType === QuestionType.CHECKBOX;

        let filterBarNode: ReactNode = null;
        let shouldHideUntilFiltered = false;

        if (isChoiceType) {
          const { selectedFilters, filterText } = getQuestionFilterState(question.id);
          const allChoices = getQuestionChoices(question);
          const visibleChoices = filterChoices(allChoices, selectedFilters, filterText);
          shouldHideUntilFiltered = shouldHideOptionsUntilFiltered(
            question.requiresFilterToShowOptions,
            allChoices,
            question.allowsTypedFilter,
            selectedFilters,
            filterText
          );

          filterBarNode = (
            <QuestionFilterBar
              choices={allChoices}
              allowsTypedFilter={Boolean(question.allowsTypedFilter)}
              selectedFilters={selectedFilters}
              filterText={filterText}
              onSelectedFiltersChange={(filters) => setQuestionSelectedFilters(question.id, filters)}
              onFilterTextChange={(text) => setQuestionFilterText(question.id, text)}
              hasVisibleResults={visibleChoices.length > 0}
              hiddenOptionsCount={shouldHideUntilFiltered ? countRegularChoices(allChoices) : undefined}
              filterLabel={question.filterLabel}
            />
          );
        }

        return (
          <Card key={question.id} className="shadow-sm hover:shadow-md transition-shadow">
            <div className="mb-4">
              <h3 className="text-lg font-bold underline text-gray-900 mb-2">
                {index + 1}. {question.shortDescription}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </h3>
              {question.longDescription && (
                <p className="text-sm text-gray-500">{question.longDescription}</p>
              )}
            </div>
            {filterBarNode}
            <div>{renderQuestion(question, shouldHideUntilFiltered)}</div>
          </Card>
        );
      })}

        {/* Bottom group nav: mirrors the top nav so users don't have to scroll back up */}
        {hasGroups && (
          <Card className="shadow-md">
            <QuestionGroupNav
              currentGroupIndex={currentGroupIdx}
              totalGroups={groupSequence!.totalGroups}
              isFirstGroup={currentGroupIdx === 0}
              isLastGroup={isLastGroup}
              canAdvance={groupCanAdvance}
              loading={submitting}
              onNext={handleGroupNext}
              onPrev={handleGroupPrev}
              onSubmit={handleSubmit}
            />
          </Card>
        )}

        {/* Submit Section (only shown in flat / non-group mode) */}
        {!hasGroups && (
          <Card className="shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{answers.size}</span> de{' '}
              <span className="font-semibold">{questions.length}</span> perguntas respondidas
            </div>
            <Space>
              <Button
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/surveys')}
              >
                Voltar
              </Button>
              <Button
                type="primary"
                size="large"
                icon={submitting ? <LoadingOutlined /> : <CheckCircleOutlined />}
                onClick={handleSubmit}
                disabled={submitting || answers.size === 0}
                loading={submitting}
              >
                {submitting ? 'Enviando...' : 'Enviar Respostas'}
              </Button>
            </Space>
          </div>
        </Card>
        )}

      {/* Error Alert */}
      {error && (
        <Alert type="error" message="Erro" description={error} closable />
      )}
      </Space>

      <Modal
        open={geoModalVisible}
        title={
          <Space>
            <EnvironmentOutlined style={{ color: '#2E2B81' }} />
            Compartilhar localização?
          </Space>
        }
        onCancel={() => handleGeoChoice(false)}
        footer={[
          <Button key="skip" onClick={() => handleGeoChoice(false)}>
            Continuar sem localizar
          </Button>,
          <Button
            key="allow"
            type="primary"
            icon={<EnvironmentOutlined />}
            onClick={() => handleGeoChoice(true)}
            style={{ background: '#2E2B81', borderColor: '#2E2B81' }}
          >
            Permitir localização
          </Button>,
        ]}
        width={460}
      >
        <Space direction="vertical" size={12} style={{ width: '100%', padding: '8px 0' }}>
          <Text>
            Para validar geograficamente sua participação nesta pesquisa, podemos
            registrar sua localização aproximada (GPS do dispositivo).
          </Text>
          <Text>
            Esta informação é utilizada exclusivamente para fins estatísticos e não é
            compartilhada individualmente. Sua participação é válida com ou sem localização.
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Base legal: LGPD Art. 7º, I — consentimento expresso. Você pode negar sem
            prejuízo à sua resposta.
          </Text>
        </Space>
      </Modal>
    </div>
  );
};

export default TakeSurvey;
